/**
 * Utility functions for validating and sanitizing IDs used in the admin panel
 */

/**
 * Sanitizes an ID string by removing spaces and converting to kebab-case format
 * @param input - The input string to sanitize
 * @returns A sanitized string suitable for use as a database ID
 */
export const sanitizeId = (input: string): string => {
  if (!input) return '';

  return input
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-zA-Z0-9-]/g, '') // Remove non-alphanumeric characters except hyphens
    .replace(/-+/g, '-'); // Replace multiple consecutive hyphens with single hyphen
  // Note: We don't remove leading/trailing hyphens during typing to allow better UX
};

/**
 * Cleans up an ID for final storage by removing leading/trailing hyphens
 * @param input - The input string to clean
 * @returns A cleaned string suitable for database storage
 */
export const cleanIdForStorage = (input: string): string => {
  if (!input) return '';
  return sanitizeId(input).replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Validates if an ID contains spaces or invalid characters
 * @param input - The input string to validate
 * @returns true if the ID is valid (no spaces, alphanumeric + hyphens only)
 */
export const isValidId = (input: string): boolean => {
  if (!input) return true; // Empty is valid (will auto-generate)

  // Check if it matches the expected pattern: alphanumeric + hyphens
  const validIdPattern = /^[a-zA-Z0-9-]+$/;
  return validIdPattern.test(input) && !input.includes(' ');
};

/**
 * Gets an error message for invalid IDs
 * @param input - The input string to check
 * @returns Error message if invalid, null if valid
 */
export const getIdValidationError = (input: string): string | null => {
  if (!input) return null; // Empty is valid

  if (input.includes(' ')) {
    return 'ID cannot contain spaces. Spaces will be converted to hyphens.';
  }

  if (!/^[a-zA-Z0-9-]+$/.test(input)) {
    return 'ID can only contain letters, numbers, and hyphens.';
  }

  return null;
};
